import { getStoreBuilder } from 'vuex-typex'
import { StatementState } from './state'
import { RootState } from '..'
import { IStatement, Statement, StatementMeta, StatementForm } from '../../model/statement'
import { IUser } from '@/model/user'
import { SearchQuery, StatementSearchForm } from '@/model/search/search-query'
import { IReference, Reference } from '@/model/reference'
import addStatement from './actions/add-statement'
import copyStatement from './actions/copy-statement'
import addStatementTag from './actions/add-statement-tag'
import removeStatementTag from './actions/remove-statement-tag'
import addStatementReference from './actions/add-statement-reference'
import removeStatementReference from './actions/remove-statement-reference'
import fetchStatements from './actions/fetch-statements'
import jybeStatement from './actions/jybe-statement'
import unjybeStatement from './actions/unjybe-statement'
import addSearchTag from './actions/add-search-tag'
import removeSearchTag from './actions/remove-search-tag'
import toggleCreatedByUser from './actions/created-by-user'
import toggleCreatedByMe from './actions/created-by-me'
import toggleCreatedByFollowing from './actions/created-by-following'
import toggleJybedByUser from './actions/jybed-by-user'
import toggleJybedByMe from './actions/jybed-by-me'
import toggleConsideredByMe from './actions/considered-by-me'
import toggleIgnoredByMe from './actions/ignored-by-me'
import toggleNotReactedByMe from './actions/not-reacted-by-me'
import toggleJybedByFollowing from './actions/jybed-by-following'
import setStatementQuery from './actions/set-statement-query'
import considerStatement from './actions/consider-statement'
import unconsiderStatement from './actions/unconsider-statement'
import ignoreStatement from './actions/ignore-statement'
import unignoreStatement from './actions/unignore-statement'
import hideStatement from './actions/hide-statement'
import setMeta from './actions/set-meta'
import fetchReactions from './actions/fetch-reactions'
import resetStatementForm from './actions/reset-statement-form'
import initFetchStatements from '@/store/statement/actions/init-fetch-statements'

const initialState: StatementState = {
  statements: [],
  statementMap: new Map<number, IStatement>(),
  statementsStatus: 0,
  statement: new Statement(),
  statementStatus: 0,
  statementForm: new StatementForm(),
  statementQuery: new SearchQuery(),
  statementSearchForm: new StatementSearchForm(),
  isLoading: false
}

const storeBuilder = getStoreBuilder<RootState>()
const moduleBuilder = storeBuilder.module<StatementState>('statement', initialState)
const stateReader = moduleBuilder.state()

const statementStore = {
  get state () { return stateReader() },

  get isLoading ():boolean { return this.state.isLoading },

  commitSetIsLoading: moduleBuilder.commit((state: StatementState, payload: { isLoading: boolean }) => {
    state.isLoading = payload.isLoading
  }, 'setIsLoading'),
  commitSetStatements: moduleBuilder.commit((state: StatementState, payload: { statements: IStatement[] }) => {
    state.statements = payload.statements
  }, 'setStatements'),
  commitAddStatement: moduleBuilder.commit((state: StatementState, payload: { statement: IStatement }) => {
    state.statements.push(payload.statement)
  }, 'addStatement'),
  commitSetStatement: moduleBuilder.commit((state: StatementState, payload: { statement: IStatement }) => state.statement = payload.statement, 'setStatement'),
  commitSetNumJybes: moduleBuilder.commit((state: StatementState, payload: { statement: IStatement, numJybes: number }) => {
    console.log('Setting numjybes for statement: %j = %j', payload.statement.id, payload.numJybes)
    if (payload.statement.meta) payload.statement.meta.numJybes = payload.numJybes
  }, 'setNumJybes'),
  commitSetIsJybed: moduleBuilder.commit((state: StatementState, payload: { statement: IStatement, isJybed: boolean }) => {
    if (payload.statement.meta) payload.statement.meta.isJybed = payload.isJybed
  }, 'setIsJybed'),
  commitSetIsConsidered: moduleBuilder.commit((state: StatementState, payload: { statement: IStatement, isConsidered: boolean }) => {
    if (payload.statement.meta) payload.statement.meta.isConsidered = payload.isConsidered
  }, 'setIsConsidered'),
  commitSetIsIgnored: moduleBuilder.commit((state: StatementState, payload: { statement: IStatement, isIgnored: boolean }) => {
    if (payload.statement.meta) payload.statement.meta.isIgnored = payload.isIgnored
  }, 'setIsIgnored'),
  commitSetCreator: moduleBuilder.commit((state: StatementState, payload: { statement: IStatement, creator: IUser }) => {
    if (payload.statement.meta) payload.statement.meta.creator = payload.creator
  }, 'setCreator'),
  commitSetTags: moduleBuilder.commit((state: StatementState, payload: { statement: IStatement, tags: string[] }) => {
    if (payload.statement.meta) payload.statement.meta.tags = payload.tags
  }, 'setTags'),
  commitSetReferences: moduleBuilder.commit((state: StatementState, payload: { statement: IStatement, references: IReference[] }) => {
    if (payload.statement.meta) payload.statement.meta.references = payload.references
  }, 'setReferences'),
  commitAddTagToForm: moduleBuilder.commit((state: StatementState, payload: { }) => {
    if (state.statementForm.tags.indexOf(state.statementForm.tag) < 0) {
      state.statementForm.tags.push(state.statementForm.tag)
    }
    state.statementForm.tag = ''
  }, 'addTagToForm'),
  commitResetStatementTag: moduleBuilder.commit((state: StatementState, payload: { statement: IStatement }) => {
    if (payload.statement.meta) payload.statement.meta.newTag = ''
  }, 'resetStatementTag'),
  commitRemoveTagFromForm: moduleBuilder.commit((state: StatementState, payload: { tag: string }) => {
    state.statementForm.tags.splice(state.statementForm.tags.indexOf(payload.tag), 1)
  }, 'removeTagFromForm'),
  commitAddReferenceToForm: moduleBuilder.commit((state: StatementState, payload: { }) => {
    let reference: IReference = new Reference()
    reference.url = state.statementForm.referenceUrl
    reference.description = state.statementForm.referenceDesc
    state.statementForm.references.push(reference)
    state.statementForm.referenceUrl = ''
    state.statementForm.referenceDesc = ''
  }, 'addReferenceToForm'),
  commitRemoveReferenceFromForm: moduleBuilder.commit((state: StatementState, payload: { index: number }) => {
    state.statementForm.references.splice(payload.index, 1)
  }, 'removeReferenceFromForm'),
  commitResetStatementReference: moduleBuilder.commit((state: StatementState, payload: { statement: IStatement }) => {
    if (payload.statement.meta) payload.statement.meta.newUrl = ''
    if (payload.statement.meta) payload.statement.meta.newDesc = ''
  }, 'resetStatementReference'),
  commitResetStatementForm: moduleBuilder.commit((state: StatementState, payload: { }) => {
    state.statementForm = new StatementForm()
  }, 'resetStatementForm'),
  commitCopyStatement: moduleBuilder.commit((state: StatementState, payload: { statement: IStatement }) => {
    const meta = payload.statement.meta as StatementMeta;
    const references = meta.references
    const tags = meta.tags
    state.statementForm = new StatementForm()
    for (let i = 0; i < references.length; i++) {
      state.statementForm.references.push(references[i])
    }
    for (let i = 0; i < tags.length; i++) {
      state.statementForm.tags.push(tags[i])
    }
  }, 'copyStatement'),
  commitSetQuery: moduleBuilder.commit((state: StatementState, payload: { query: SearchQuery }) => {
    state.statementQuery = payload.query
  }, 'setStatementQuery'),
/* 
  commitSetSearchUser: moduleBuilder.commit((state: StatementState, payload: { user: IUser }) => {
    state.statementSearchForm.searchUser = payload.user
  }, 'setSearchUser'),
 */
  dispatchSetStatementQuery: moduleBuilder.dispatch(setStatementQuery),
  dispatchAddStatement: moduleBuilder.dispatch(addStatement),
  dispatchCopyStatement: moduleBuilder.dispatch(copyStatement),
  dispatchAddStatementTag: moduleBuilder.dispatch(addStatementTag),
  dispatchRemoveStatementTag: moduleBuilder.dispatch(removeStatementTag),
  dispatchAddStatementReference: moduleBuilder.dispatch(addStatementReference),
  dispatchRemoveStatementReference: moduleBuilder.dispatch(removeStatementReference),
  dispatchFetchStatements: moduleBuilder.dispatch(fetchStatements),
  dispatchJybeStatement: moduleBuilder.dispatch(jybeStatement),
  dispatchUnjybeStatement: moduleBuilder.dispatch(unjybeStatement),
  dispatchConsiderStatement: moduleBuilder.dispatch(considerStatement),
  dispatchUnconsiderStatement: moduleBuilder.dispatch(unconsiderStatement),
  dispatchIgnoreStatement: moduleBuilder.dispatch(ignoreStatement),
  dispatchUnignoreStatement: moduleBuilder.dispatch(unignoreStatement),
  dispatchFetchReactions: moduleBuilder.dispatch(fetchReactions),
  dispatchAddSearchTag: moduleBuilder.dispatch(addSearchTag),
  dispatchRemoveSearchTag: moduleBuilder.dispatch(removeSearchTag),
  dispatchToggleCreatedByMe: moduleBuilder.dispatch(toggleCreatedByMe),
  dispatchToggleCreatedByUser: moduleBuilder.dispatch(toggleCreatedByUser),
  dispatchToggleCreatedByFollowing: moduleBuilder.dispatch(toggleCreatedByFollowing),
  dispatchToggleJybedByUser: moduleBuilder.dispatch(toggleJybedByUser),
  dispatchToggleJybedByMe: moduleBuilder.dispatch(toggleJybedByMe),
  dispatchToggleConsideredByMe: moduleBuilder.dispatch(toggleConsideredByMe),
  dispatchToggleIgnoredByMe: moduleBuilder.dispatch(toggleIgnoredByMe),
  dispatchToggleNotReactedByMe: moduleBuilder.dispatch(toggleNotReactedByMe),
  dispatchToggleJybedByFollowing: moduleBuilder.dispatch(toggleJybedByFollowing),
  dispatchHideStatement: moduleBuilder.dispatch(hideStatement),
  dispatchSetMeta: moduleBuilder.dispatch(setMeta),
  dispatchResetStatementForm: moduleBuilder.dispatch(resetStatementForm),
  dispatchInitFetchStatements: moduleBuilder.dispatch(initFetchStatements),
}

export default statementStore